import {useCallback, useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import * as XLSX from 'xlsx'
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CalendarRange,
  Clock3,
  Download,
  Eye,
  FileSpreadsheet,
  Gauge,
  Layers3,
  MousePointerClick,
  Radio,
  RefreshCw,
  Search,
  Users,
  Zap,
} from 'lucide-react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  AnalyticsFilters,
  AnalyticsSection,
  getAnalyticsFilters,
  getAnalyticsSection,
} from './analyticsApi'
import './analytics.css'

const COLORS = ['#635bff', '#12b76a', '#f79009', '#2e90fa', '#ee46bc', '#7a5af8']

const dateInputValue = (date: Date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}

const defaultFilters = (): AnalyticsFilters => {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 29)
  return {from: dateInputValue(from), to: dateInputValue(to)}
}

const formatNumber = (value: unknown) => new Intl.NumberFormat('pt-BR').format(Number(value) || 0)

const formatDuration = (value: unknown) => {
  const milliseconds = Number(value) || 0
  if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`
  const seconds = milliseconds / 1000
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} s`
  const minutes = Math.floor(seconds / 60)
  const remaining = Math.round(seconds % 60)
  return `${minutes}m ${remaining}s`
}

const formatDateTime = (value: unknown) => {
  if (!value) return '-'
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('pt-BR', {dateStyle: 'short', timeStyle: 'short'})
}

const compactLabel = (value: unknown, size = 28) => {
  const text = String(value || '-')
  return text.length > size ? `${text.slice(0, size - 1)}...` : text
}

const sectionDefinitions: Array<{
  id: AnalyticsSection
  label: string
  description: string
  icon: typeof Activity
}> = [
  {id: 'overview', label: 'Visão geral', description: 'Saúde e adoção do sistema', icon: BarChart3},
  {id: 'pages', label: 'Telas', description: 'Acessos e permanência', icon: Eye},
  {id: 'users', label: 'Usuários', description: 'Engajamento individual', icon: Users},
  {id: 'features', label: 'Features', description: 'Ações mais utilizadas', icon: MousePointerClick},
  {id: 'performance', label: 'Performance', description: 'APIs, erros e Web Vitals', icon: Gauge},
  {id: 'realtime', label: 'Tempo real', description: 'Atividade nos últimos 30 minutos', icon: Radio},
]

const MetricCard = ({
  label,
  value,
  helper,
  icon: Icon,
  tone = 'purple',
  live = false,
}: {
  label: string
  value: string
  helper: string
  icon: typeof Activity
  tone?: string
  live?: boolean
}) => (
  <article className={`analytics-metric analytics-metric--${tone}`}>
    <div className='analytics-metric__top'>
      <span className='analytics-metric__icon'><Icon size={19} /></span>
      {live && <span className='analytics-live-pill'><i /> últimos 30 min</span>}
    </div>
    <strong>{value}</strong>
    <span className='analytics-metric__label'>{label}</span>
    <small>{helper}</small>
  </article>
)

const Panel = ({
  title,
  subtitle,
  children,
  className = '',
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
  className?: string
}) => (
  <section className={`analytics-panel ${className}`}>
    <header className='analytics-panel__header'>
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </header>
    <div className='analytics-panel__body'>{children}</div>
  </section>
)

const EmptyState = ({label = 'Ainda não há dados para este recorte.'}: {label?: string}) => (
  <div className='analytics-empty'>
    <span><Layers3 size={22} /></span>
    <strong>Coleta em andamento</strong>
    <p>{label}</p>
  </div>
)

const ChartTooltip = ({active, payload, label}: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className='analytics-chart-tooltip'>
      <strong>{label}</strong>
      {payload.map((item: any) => (
        <span key={item.dataKey} style={{color: item.color}}>
          {item.name}: {formatNumber(item.value)}
        </span>
      ))}
    </div>
  )
}

const Overview = ({data}: {data: any}) => {
  const summary = data?.summary || {}
  const activeWindowMinutes = Number(summary.active_window_minutes) || 30
  const timeline = data?.timeline || []
  const pages = data?.top_pages || []
  const features = data?.top_features || []
  const devices = data?.devices || []
  const activity = data?.activity || []

  return (
    <>
      <div className='analytics-metrics-grid'>
        <MetricCard label='Usuários ativos' value={formatNumber(summary.active_users)} helper={`${formatNumber(summary.active_sessions)} sessões nos últimos ${activeWindowMinutes} min`} icon={Radio} tone='green' live />
        <MetricCard label='Visualizações' value={formatNumber(summary.page_views)} helper={`${formatNumber(summary.sessions)} sessões no período`} icon={Eye} tone='purple' />
        <MetricCard label='Usuários únicos' value={formatNumber(summary.unique_users)} helper='Pessoas que acessaram o sistema' icon={Users} tone='blue' />
        <MetricCard label='Tempo médio por tela' value={formatDuration(summary.avg_page_duration_ms)} helper='Permanência média por navegação' icon={Clock3} tone='orange' />
        <MetricCard label='Resposta P95' value={formatDuration(summary.p95_api_duration_ms)} helper={`Média de ${formatDuration(summary.avg_api_duration_ms)}`} icon={Zap} tone='pink' />
        <MetricCard label='Taxa de erros' value={`${Number(summary.error_rate || 0).toFixed(2)}%`} helper={`${formatNumber(summary.errors)} ocorrências detectadas`} icon={AlertTriangle} tone={summary.error_rate > 5 ? 'red' : 'slate'} />
      </div>

      <div className='analytics-grid analytics-grid--wide'>
        <Panel title='Evolução de uso' subtitle='Visualizações, usuários e uso de features ao longo do tempo' className='analytics-panel--span-2'>
          {timeline.length ? (
            <ResponsiveContainer width='100%' height={310}>
              <AreaChart data={timeline} margin={{top: 12, right: 8, left: -18, bottom: 0}}>
                <defs>
                  <linearGradient id='viewsGradient' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#635bff' stopOpacity={0.28} />
                    <stop offset='95%' stopColor='#635bff' stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id='usersGradient' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#12b76a' stopOpacity={0.2} />
                    <stop offset='95%' stopColor='#12b76a' stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e9edf5' />
                <XAxis dataKey='bucket' tick={{fontSize: 11, fill: '#667085'}} tickLine={false} axisLine={false} />
                <YAxis tick={{fontSize: 11, fill: '#667085'}} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType='circle' wrapperStyle={{fontSize: 12, paddingTop: 12}} />
                <Area type='monotone' dataKey='page_views' name='Visualizações' stroke='#635bff' strokeWidth={2.5} fill='url(#viewsGradient)' />
                <Area type='monotone' dataKey='users' name='Usuários' stroke='#12b76a' strokeWidth={2.2} fill='url(#usersGradient)' />
                <Area type='monotone' dataKey='feature_uses' name='Features' stroke='#f79009' strokeWidth={2} fill='transparent' />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </Panel>

        <Panel title='Dispositivos' subtitle='Distribuição das visualizações'>
          {devices.length ? (
            <div className='analytics-donut'>
              <ResponsiveContainer width='100%' height={230}>
                <PieChart>
                  <Pie data={devices} dataKey='total' nameKey='device' innerRadius={58} outerRadius={88} paddingAngle={4}>
                    {devices.map((_: any, index: number) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                </PieChart>
              </ResponsiveContainer>
              <div className='analytics-legend'>
                {devices.map((item: any, index: number) => (
                  <span key={item.device}><i style={{background: COLORS[index % COLORS.length]}} />{item.device}<strong>{formatNumber(item.total)}</strong></span>
                ))}
              </div>
            </div>
          ) : <EmptyState />}
        </Panel>
      </div>

      <div className='analytics-grid analytics-grid--thirds'>
        <Panel title='Telas mais acessadas' subtitle='Ranking por visualizações' className='analytics-panel--span-2'>
          {pages.length ? (
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={pages.slice(0, 8)} layout='vertical' margin={{top: 4, right: 18, left: 16, bottom: 0}}>
                <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke='#eef1f6' />
                <XAxis type='number' hide />
                <YAxis type='category' dataKey='page_name' width={132} tickFormatter={(value) => compactLabel(value, 20)} tick={{fontSize: 11, fill: '#475467'}} tickLine={false} axisLine={false} />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Bar dataKey='views' name='Visualizações' fill='#635bff' radius={[0, 7, 7, 0]} barSize={17} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </Panel>

        <Panel title='Atividade recente' subtitle='Últimos eventos relevantes'>
          {activity.length ? (
            <div className='analytics-activity-list'>
              {activity.slice(0, 10).map((item: any, index: number) => (
                <div className='analytics-activity' key={`${item.occurred_at}-${index}`}>
                  <span className={`analytics-activity__dot analytics-activity__dot--${item.event_type}`} />
                  <div>
                    <strong>{item.user_name || item.user_email || 'Usuário'}</strong>
                    <p>{item.event_type === 'page_view' ? 'acessou' : item.event_type === 'feature_use' ? 'utilizou' : 'gerou um erro'} <b>{compactLabel(item.event_name, 36)}</b></p>
                    <small>{formatDateTime(item.occurred_at)}</small>
                  </div>
                </div>
              ))}
            </div>
          ) : <EmptyState />}
        </Panel>
      </div>

      <Panel title='Features mais utilizadas' subtitle='Ações capturadas automaticamente em botões, links e controles'>
        {features.length ? (
          <div className='analytics-ranking-grid'>
            {features.map((feature: any, index: number) => (
              <div className='analytics-ranking' key={feature.feature_name}>
                <span>{index + 1}</span>
                <div><strong>{feature.feature_name}</strong><small>{formatNumber(feature.users)} usuários</small></div>
                <b>{formatNumber(feature.uses)} usos</b>
              </div>
            ))}
          </div>
        ) : <EmptyState label='As features aparecerão assim que os usuários interagirem com o sistema.' />}
      </Panel>
    </>
  )
}

const PagesView = ({data, search}: {data: any; search: string}) => {
  const pages = (data?.pages || []).filter((page: any) =>
    `${page.page_name} ${page.path}`.toLowerCase().includes(search.toLowerCase())
  )
  return (
    <>
      <Panel title='Comparativo de telas' subtitle='Volume das 12 telas mais acessadas'>
        {pages.length ? (
          <ResponsiveContainer width='100%' height={320}>
            <BarChart data={pages.slice(0, 12)} margin={{top: 10, right: 10, left: -12, bottom: 54}}>
              <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#edf0f5' />
              <XAxis dataKey='page_name' angle={-32} textAnchor='end' interval={0} height={75} tickFormatter={(value) => compactLabel(value, 17)} tick={{fontSize: 10, fill: '#667085'}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize: 11, fill: '#667085'}} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey='views' name='Visualizações' fill='#635bff' radius={[7, 7, 0, 0]} />
              <Bar dataKey='users' name='Usuários' fill='#b9b5ff' radius={[7, 7, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState />}
      </Panel>
      <Panel title='Relatório por tela' subtitle={`${formatNumber(pages.length)} telas encontradas`}>
        <DataTable
          columns={[
            {label: 'Tela', render: (row) => <div className='analytics-primary-cell'><strong>{row.page_name || row.path}</strong><small>{row.path}</small></div>},
            {label: 'Visualizações', render: (row) => formatNumber(row.views)},
            {label: 'Usuários', render: (row) => formatNumber(row.users)},
            {label: 'Sessões', render: (row) => formatNumber(row.sessions)},
            {label: 'Tempo médio', render: (row) => formatDuration(row.avg_duration_ms)},
            {label: 'Rejeição', render: (row) => `${Number(row.bounce_rate || 0).toFixed(1)}%`},
            {label: 'Erros', render: (row) => <StatusValue value={row.errors} danger />},
            {label: 'Último acesso', render: (row) => formatDateTime(row.last_access)},
          ]}
          rows={pages}
        />
      </Panel>
    </>
  )
}

const UsersView = ({data, search}: {data: any; search: string}) => {
  const users = (data?.users || []).filter((user: any) =>
    `${user.user_name} ${user.user_email} ${user.company_name}`.toLowerCase().includes(search.toLowerCase())
  )
  return (
    <>
      <div className='analytics-grid analytics-grid--wide'>
        <Panel title='Usuários mais ativos' subtitle='Ranking por visualizações' className='analytics-panel--span-2'>
          {users.length ? (
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={users.slice(0, 10)} layout='vertical' margin={{top: 4, right: 16, left: 30, bottom: 0}}>
                <XAxis type='number' hide />
                <YAxis type='category' dataKey='user_name' width={140} tickFormatter={(value) => compactLabel(value, 20)} tick={{fontSize: 11, fill: '#475467'}} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey='page_views' name='Visualizações' fill='#2e90fa' radius={[0, 7, 7, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </Panel>
        <div className='analytics-insight-card'>
          <span><Users size={22} /></span>
          <p>Usuários monitorados</p>
          <strong>{formatNumber(users.length)}</strong>
          <small>com atividade no período selecionado</small>
        </div>
      </div>
      <Panel title='Usabilidade por usuário' subtitle='Engajamento, recorrência e preferência de navegação'>
        <DataTable
          columns={[
            {label: 'Usuário', render: (row) => <div className='analytics-user-cell'><Avatar name={row.user_name} /><div><strong>{row.user_name}</strong><small>{row.user_email}</small></div></div>},
            {label: 'Empresa', render: (row) => row.company_name || '-'},
            {label: 'Visualizações', render: (row) => formatNumber(row.page_views)},
            {label: 'Sessões', render: (row) => formatNumber(row.sessions)},
            {label: 'Dias ativos', render: (row) => formatNumber(row.active_days)},
            {label: 'Features', render: (row) => formatNumber(row.feature_uses)},
            {label: 'Tela favorita', render: (row) => compactLabel(row.top_page?.page_name || row.top_page?.path, 24)},
            {label: 'Última atividade', render: (row) => formatDateTime(row.last_activity)},
          ]}
          rows={users}
        />
      </Panel>
    </>
  )
}

const FeaturesView = ({data, search}: {data: any; search: string}) => {
  const features = (data?.features || []).filter((feature: any) =>
    `${feature.feature_name} ${feature.path}`.toLowerCase().includes(search.toLowerCase())
  )
  return (
    <>
      <Panel title='Adoção de features' subtitle='Ações mais utilizadas no período'>
        {features.length ? (
          <ResponsiveContainer width='100%' height={350}>
            <BarChart data={features.slice(0, 15)} margin={{top: 10, right: 10, left: -12, bottom: 70}}>
              <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#edf0f5' />
              <XAxis dataKey='feature_name' angle={-36} textAnchor='end' interval={0} height={94} tickFormatter={(value) => compactLabel(value, 18)} tick={{fontSize: 10, fill: '#667085'}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize: 11, fill: '#667085'}} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey='uses' name='Usos' fill='#7a5af8' radius={[7, 7, 0, 0]} />
              <Bar dataKey='users' name='Usuários' fill='#d6bbfb' radius={[7, 7, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState />}
      </Panel>
      <Panel title='Catálogo de uso' subtitle={`${formatNumber(features.length)} combinações de feature e tela`}>
        <DataTable
          columns={[
            {label: 'Feature', render: (row) => <div className='analytics-feature-cell'><span><MousePointerClick size={15} /></span><strong>{row.feature_name}</strong></div>},
            {label: 'Tela', render: (row) => <span title={row.path}>{compactLabel(row.path, 35)}</span>},
            {label: 'Total de usos', render: (row) => <b>{formatNumber(row.uses)}</b>},
            {label: 'Usuários únicos', render: (row) => formatNumber(row.users)},
            {label: 'Sessões', render: (row) => formatNumber(row.sessions)},
            {label: 'Último uso', render: (row) => formatDateTime(row.last_use)},
          ]}
          rows={features}
        />
      </Panel>
    </>
  )
}

const PerformanceView = ({data}: {data: any}) => {
  const summary = data?.summary || {}
  const endpoints = data?.endpoints || []
  const vitals = data?.web_vitals || []
  const errors = data?.recent_errors || []
  return (
    <>
      <div className='analytics-metrics-grid analytics-metrics-grid--four'>
        <MetricCard label='Requisições' value={formatNumber(summary.requests)} helper='Chamadas processadas pelo backend' icon={Activity} tone='purple' />
        <MetricCard label='Tempo médio' value={formatDuration(summary.avg_ms)} helper='Latência média do servidor' icon={Clock3} tone='blue' />
        <MetricCard label='Percentil 95' value={formatDuration(summary.p95_ms)} helper='95% das respostas abaixo deste valor' icon={Gauge} tone='orange' />
        <MetricCard label='Erros de API' value={`${Number(summary.error_rate || 0).toFixed(2)}%`} helper={`${formatNumber(summary.errors)} requisições com falha`} icon={AlertTriangle} tone={summary.error_rate > 5 ? 'red' : 'slate'} />
      </div>

      <div className='analytics-grid analytics-grid--wide'>
        <Panel title='Endpoints mais lentos' subtitle='Ordenados pela latência P95' className='analytics-panel--span-2'>
          {endpoints.length ? (
            <ResponsiveContainer width='100%' height={310}>
              <BarChart data={endpoints.slice(0, 10)} layout='vertical' margin={{top: 4, right: 20, left: 40, bottom: 0}}>
                <XAxis type='number' tickFormatter={(value) => `${value}ms`} tick={{fontSize: 10, fill: '#667085'}} axisLine={false} tickLine={false} />
                <YAxis type='category' dataKey='endpoint' width={180} tickFormatter={(value) => compactLabel(value, 25)} tick={{fontSize: 10, fill: '#475467'}} axisLine={false} tickLine={false} />
                <Tooltip formatter={(value: number) => formatDuration(value)} />
                <Bar dataKey='p95_ms' name='P95' fill='#f79009' radius={[0, 7, 7, 0]} barSize={17} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </Panel>

        <Panel title='Web Vitals' subtitle='Experiência percebida no navegador'>
          {vitals.length ? (
            <div className='analytics-vitals'>
              {vitals.map((vital: any) => (
                <div key={vital.metric}>
                  <span>{vital.metric}</span>
                  <strong>{vital.metric.includes('CLS') ? (vital.average / 1000).toFixed(3) : formatDuration(vital.average)}</strong>
                  <small>{formatNumber(vital.samples)} amostras</small>
                </div>
              ))}
            </div>
          ) : <EmptyState label='Os Web Vitals aparecerão após os próximos carregamentos do sistema.' />}
        </Panel>
      </div>

      <Panel title='Performance por endpoint' subtitle='Volume, média, percentil 95, pico e taxa de erro'>
        <DataTable
          columns={[
            {label: 'Endpoint', render: (row) => <div className='analytics-endpoint'><span className={`analytics-method analytics-method--${String(row.method).toLowerCase()}`}>{row.method}</span><code>{row.path}</code></div>},
            {label: 'Requisições', render: (row) => formatNumber(row.requests)},
            {label: 'Média', render: (row) => formatDuration(row.avg_ms)},
            {label: 'P95', render: (row) => <b>{formatDuration(row.p95_ms)}</b>},
            {label: 'Máximo', render: (row) => formatDuration(row.max_ms)},
            {label: 'Taxa de erro', render: (row) => <StatusValue value={row.error_rate} suffix='%' danger />},
          ]}
          rows={endpoints}
        />
      </Panel>

      <Panel title='Erros recentes' subtitle='Falhas de API e exceções capturadas no navegador'>
        {errors.length ? (
          <div className='analytics-error-list'>
            {errors.slice(0, 30).map((error: any, index: number) => (
              <div key={`${error.occurred_at}-${index}`}>
                <span><AlertTriangle size={16} /></span>
                <div><strong>{error.event_name || error.path}</strong><p>{error.user_name || error.user_email || 'Usuário'} em {error.path || '-'}</p></div>
                <b>{error.status_code || 'JS'}</b>
                <small>{formatDateTime(error.occurred_at)}</small>
              </div>
            ))}
          </div>
        ) : <div className='analytics-success-empty'><Zap size={20} /><div><strong>Nenhum erro no período</strong><p>O sistema não registrou falhas neste recorte.</p></div></div>}
      </Panel>
    </>
  )
}

const RealtimeView = ({data}: {data: any}) => {
  const sessions = data?.sessions || []
  const pages = data?.pages || []
  const activeWindowMinutes = Number(data?.active_window_minutes) || 30
  return (
    <>
      <div className='analytics-realtime-hero'>
        <div className='analytics-realtime-hero__pulse'><i /><Radio size={25} /></div>
        <div><span>Usuários ativos</span><strong>{formatNumber(data?.active_users)}</strong><p>Atividade nos últimos {activeWindowMinutes} minutos • atualização a cada 15 segundos</p></div>
        <div className='analytics-realtime-hero__stats'>
          <span><b>{formatNumber(data?.active_sessions)}</b> sessões ativas</span>
          <span><b>{formatNumber(data?.background_sessions)}</b> em segundo plano</span>
        </div>
      </div>

      <div className='analytics-grid analytics-grid--wide'>
        <Panel title='Atividade por tela' subtitle={`Usuários ativos nos últimos ${activeWindowMinutes} minutos`} className='analytics-panel--span-2'>
          {pages.length ? (
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={pages.slice(0, 12)} layout='vertical' margin={{top: 4, right: 24, left: 32, bottom: 0}}>
                <XAxis type='number' allowDecimals={false} tick={{fontSize: 10}} axisLine={false} tickLine={false} />
                <YAxis type='category' dataKey='page_name' width={150} tickFormatter={(value) => compactLabel(value, 22)} tick={{fontSize: 11, fill: '#475467'}} axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey='users' name='Usuários' fill='#12b76a' radius={[0, 7, 7, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState label={`Não há usuários ativos nos últimos ${activeWindowMinutes} minutos.`} />}
        </Panel>
        <div className='analytics-now-card'>
          <span className='analytics-now-card__status'><i /> sistema monitorado</span>
          <Activity size={34} />
          <strong>{formatDateTime(data?.generated_at)}</strong>
          <p>Última sincronização de presença</p>
        </div>
      </div>

      <Panel title='Sessões conectadas' subtitle='Presença por usuário, tela e dispositivo'>
        <DataTable
          columns={[
            {label: 'Status', render: (row) => <span className={`analytics-presence ${Number(row.is_visible) === 1 ? 'is-online' : ''}`}><i />{Number(row.is_visible) === 1 ? 'Ativo' : 'Em segundo plano'}</span>},
            {label: 'Usuário', render: (row) => <div className='analytics-user-cell'><Avatar name={row.user_name} /><div><strong>{row.user_name}</strong><small>{row.user_email}</small></div></div>},
            {label: 'Empresa', render: (row) => row.company_name || '-'},
            {label: 'Tela atual', render: (row) => <div className='analytics-primary-cell'><strong>{row.page_name || row.current_path}</strong><small>{row.current_path}</small></div>},
            {label: 'Dispositivo', render: (row) => `${row.device_type || '-'} / ${row.browser_name || '-'}`},
            {label: 'Conectado desde', render: (row) => formatDateTime(row.started_at)},
            {label: 'Último sinal', render: (row) => formatDateTime(row.last_seen)},
          ]}
          rows={sessions}
        />
      </Panel>
    </>
  )
}

const Avatar = ({name}: {name: string}) => {
  const initials = String(name || 'U').split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
  return <span className='analytics-avatar'>{initials}</span>
}

const StatusValue = ({value, suffix = '', danger = false}: {value: unknown; suffix?: string; danger?: boolean}) => {
  const number = Number(value) || 0
  return <span className={danger && number > 0 ? 'analytics-value--danger' : 'analytics-value--ok'}>{number.toFixed(suffix ? 2 : 0)}{suffix}</span>
}

const DataTable = ({
  columns,
  rows,
}: {
  columns: Array<{label: string; render: (row: any) => React.ReactNode}>
  rows: any[]
}) => {
  if (!rows.length) return <EmptyState />
  return (
    <div className='analytics-table-wrap'>
      <table className='analytics-table'>
        <thead><tr>{columns.map((column) => <th key={column.label}>{column.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.user_guid || row.path || row.feature_name || row.endpoint || row.session_id || 'row'}-${index}`}>
              {columns.map((column) => <td key={column.label}>{column.render(row)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const getExportSheets = (section: AnalyticsSection, data: any) => {
  const definitions: Record<AnalyticsSection, Array<[string, any]>> = {
    overview: [
      ['Resumo', data?.summary ? [data.summary] : []],
      ['Evolução', data?.timeline],
      ['Telas', data?.top_pages],
      ['Features', data?.top_features],
      ['Dispositivos', data?.devices],
      ['Atividade', data?.activity],
    ],
    pages: [['Telas', data?.pages]],
    users: [['Usuários', data?.users]],
    features: [['Features', data?.features]],
    performance: [
      ['Endpoints', data?.endpoints],
      ['Web Vitals', data?.web_vitals],
      ['Erros', data?.errors],
    ],
    realtime: [
      ['Sessões online', data?.sessions],
      ['Telas ativas', data?.pages],
    ],
  }

  return definitions[section]
    .filter(([, rows]) => Array.isArray(rows) && rows.length)
    .map(([name, rows]) => ({
      name,
      rows: rows.map((row: any) => Object.fromEntries(
        Object.entries(row).map(([key, value]) => [
          key,
          value && typeof value === 'object' ? JSON.stringify(value) : value,
        ])
      )),
    }))
}

const AnalyticsPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const routeSection = location.pathname.split('/')[2] as AnalyticsSection
  const activeSection = sectionDefinitions.some((section) => section.id === routeSection) ? routeSection : 'overview'
  const activeDefinition = sectionDefinitions.find((section) => section.id === activeSection) || sectionDefinitions[0]

  const [filters, setFilters] = useState<AnalyticsFilters>(defaultFilters)
  const [filterOptions, setFilterOptions] = useState<any>({users: [], pages: [], companies: []})
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [search, setSearch] = useState('')

  const loadData = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true)
    setError('')
    try {
      const response = await getAnalyticsSection(activeSection, filters)
      if (!response.success) throw new Error(response.error || 'Falha ao carregar Analytics')
      setData(response.data)
    } catch (loadError: any) {
      setError(loadError?.response?.status === 403 ? 'Você não possui permissão para visualizar o Analytics.' : 'Não foi possível carregar os dados de Analytics.')
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [activeSection, filters])

  useEffect(() => {
    void loadData()
  }, [loadData, refreshKey])

  useEffect(() => {
    void getAnalyticsFilters(filters.company_guid)
      .then((response) => response.success && setFilterOptions(response.data))
      .catch(() => undefined)
  }, [filters.company_guid])

  useEffect(() => {
    if (activeSection !== 'realtime') return
    const interval = window.setInterval(() => void loadData(true), 15000)
    return () => window.clearInterval(interval)
  }, [activeSection, loadData])

  const updateFilter = (key: keyof AnalyticsFilters, value: string) => {
    setFilters((current) => ({...current, [key]: value || undefined}))
  }

  const setPeriod = (days: number) => {
    const to = new Date()
    const from = new Date()
    from.setDate(from.getDate() - (days - 1))
    setFilters((current) => ({...current, from: dateInputValue(from), to: dateInputValue(to)}))
  }

  const exportCsv = () => {
    const rows =
      activeSection === 'pages' ? data?.pages :
      activeSection === 'users' ? data?.users :
      activeSection === 'features' ? data?.features :
      activeSection === 'performance' ? data?.endpoints :
      activeSection === 'realtime' ? data?.sessions :
      data?.top_pages
    if (!Array.isArray(rows) || !rows.length) return
    const keys = Array.from(new Set(rows.flatMap((row: any) => Object.keys(row).filter((key) => typeof row[key] !== 'object'))))
    const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`
    const csv = [keys.join(';'), ...rows.map((row: any) => keys.map((key) => escape(row[key])).join(';'))].join('\n')
    const blob = new Blob([`\ufeff${csv}`], {type: 'text/csv;charset=utf-8'})
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `analytics-${activeSection}-${filters.from}-${filters.to}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const exportXlsx = () => {
    const sheets = getExportSheets(activeSection, data)
    if (!sheets.length) return

    const workbook = XLSX.utils.book_new()
    sheets.forEach(({name, rows}) => {
      const worksheet = XLSX.utils.json_to_sheet(rows)
      const keys: string[] = Array.from(new Set<string>(rows.flatMap((row: any) => Object.keys(row))))
      worksheet['!cols'] = keys.map((key) => ({
        wch: Math.min(45, Math.max(key.length + 2, ...rows.map((row: any) => String(row[key] ?? '').length + 2))),
      }))
      XLSX.utils.book_append_sheet(workbook, worksheet, name.slice(0, 31))
    })

    XLSX.writeFile(workbook, `analytics-${activeSection}-${filters.from}-${filters.to}.xlsx`, {compression: true})
  }

  const renderContent = () => {
    if (loading) return <div className='analytics-loading'><span /><strong>Preparando seus indicadores...</strong><p>Consolidando eventos, usuários e performance</p></div>
    if (error) return <div className='analytics-error'><AlertTriangle size={24} /><div><strong>Analytics indisponível</strong><p>{error}</p></div><button onClick={() => setRefreshKey((key) => key + 1)}>Tentar novamente</button></div>
    if (activeSection === 'pages') return <PagesView data={data} search={search} />
    if (activeSection === 'users') return <UsersView data={data} search={search} />
    if (activeSection === 'features') return <FeaturesView data={data} search={search} />
    if (activeSection === 'performance') return <PerformanceView data={data} />
    if (activeSection === 'realtime') return <RealtimeView data={data} />
    return <Overview data={data} />
  }

  return (
    <div className='analytics-page'>
      <div className='analytics-hero'>
        <div>
          <span className='analytics-eyebrow'><Activity size={14} /> Intelligence center</span>
          <h1>Analytics do Sistema Interno</h1>
          <p>Entenda adoção, comportamento, performance e presença em uma única visão.</p>
        </div>
        <div className='analytics-hero__actions'>
          <button className='analytics-button analytics-button--secondary' onClick={exportXlsx} disabled={!data}>
            <FileSpreadsheet size={16} /> Exportar XLSX
          </button>
          <button className='analytics-button analytics-button--secondary' onClick={exportCsv} disabled={!data}>
            <Download size={16} /> Exportar CSV
          </button>
          <button className='analytics-button analytics-button--primary' onClick={() => setRefreshKey((key) => key + 1)}>
            <RefreshCw size={16} /> Atualiza
          </button>
        </div>
      </div>

      <nav className='analytics-tabs' aria-label='Navegação do Analytics'>
        {sectionDefinitions.map((section) => {
          const Icon = section.icon
          return (
            <button
              key={section.id}
              className={section.id === activeSection ? 'is-active' : ''}
              onClick={() => navigate(`/analytics/${section.id}`)}
            >
              <Icon size={17} />
              <span><strong>{section.label}</strong><small>{section.description}</small></span>
            </button>
          )
        })}
      </nav>

      <div className='analytics-filterbar'>
        <div className='analytics-periods'>
          <button onClick={() => setPeriod(7)}>7 dias</button>
          <button onClick={() => setPeriod(30)}>30 dias</button>
          <button onClick={() => setPeriod(90)}>90 dias</button>
        </div>
        <label><CalendarRange size={15} /><span>De</span><input type='date' value={filters.from} onChange={(event) => updateFilter('from', event.target.value)} /></label>
        <label><span>Até</span><input type='date' value={filters.to} onChange={(event) => updateFilter('to', event.target.value)} /></label>
        {filterOptions.companies?.length > 1 && (
          <label><span>Empresa</span><select value={filters.company_guid || ''} onChange={(event) => updateFilter('company_guid', event.target.value)}><option value=''>Todas</option>{filterOptions.companies.map((option: any) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        )}
        <label><span>Usuário</span><select value={filters.user_guid || ''} onChange={(event) => updateFilter('user_guid', event.target.value)}><option value=''>Todos</option>{filterOptions.users?.map((option: any) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        {(activeSection === 'pages' || activeSection === 'users' || activeSection === 'features') && (
          <label className='analytics-search'><Search size={15} /><input placeholder='Buscar neste relatório' value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        )}
      </div>

      <div className='analytics-section-heading'>
        <div><span><activeDefinition.icon size={19} /></span><div><h2>{activeDefinition.label}</h2><p>{activeDefinition.description}</p></div></div>
        <small>Período: {new Date(`${filters.from}T12:00:00`).toLocaleDateString('pt-BR')} a {new Date(`${filters.to}T12:00:00`).toLocaleDateString('pt-BR')}</small>
      </div>

      <main className='analytics-content'>{renderContent()}</main>
    </div>
  )
}

export default AnalyticsPage
